import React, { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { Button } from '@mui/material';
import MeetingService from 'services/MeetingService';
import Modal from 'components/shared/Modal';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import PasswordFormItem from 'components/shared/PasswordFormItem';
import { ModalProps } from 'store/types/ComponentProps';
import { defaultFormProps } from 'util/form';
import { defaultSnackbarErrorProps } from 'util/layout';

interface ValidatePasswordModalProps extends ModalProps {
  onSubmit: () => void;
  meetingId?: string;
}

interface ValidateMeetingFormValues {
  password: string;
}

const ValidatePasswordModal: React.FunctionComponent<ValidatePasswordModalProps> = ({
  onClose,
  open,
  onSubmit,
  meetingId,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(false);
  const ValidateMeetingForm = useForm<ValidateMeetingFormValues>({
    ...defaultFormProps,
    defaultValues: {
      password: '',
    },
  });
  const { formState, handleSubmit, reset } = ValidateMeetingForm;

  const submitButtonDisabled = !formState.isValid || !formState.isDirty || loading;

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handlePasswordSubmit = useCallback(
    (formValues: ValidateMeetingFormValues) => {
      setLoading(true);
      MeetingService.validateMeetingPassword(meetingId ?? '', formValues.password)
        .then((result: boolean) => {
          if (result) {
            reset();
            onSubmit();
          } else {
            enqueueSnackbar('Invalid password', { variant: 'error' });
          }
        })
        .catch((error: string) => {
          enqueueSnackbar('Failed to validate password', defaultSnackbarErrorProps);
          console.error('Error validating password:', error);
        })
        .finally(() => setLoading(false));
    },
    [enqueueSnackbar, meetingId, onSubmit, reset]
  );

  return (
    <Modal
      title={'Join Meeting'}
      open={open}
      maxWidth={'xs'}
      loading={loading}
      onClose={handleClose}
      onSubmit={handleSubmit(handlePasswordSubmit)}
      actions={
        <>
          <Button color={'secondary'} variant={'outlined'} disabled={loading} onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" color={'primary'} variant={'contained'} disabled={submitButtonDisabled}>
            Join
          </Button>
        </>
      }
    >
      <FormProvider {...ValidateMeetingForm}>
        <GridContainer>
          <GridItem>
            <PasswordFormItem />
          </GridItem>
        </GridContainer>
      </FormProvider>
    </Modal>
  );
};

export default ValidatePasswordModal;
